const normalizedKey = key => String(key).replace(/[^a-z0-9]/gi, '').toLowerCase();
const asText = value => (typeof value === 'string' || typeof value === 'number') ? String(value).trim() : '';

export function referralDateOfBirth(data = {}, schema = null) {
  const aliases = new Set(['dob', 'dateofbirth', 'birthdate', 'datebirth', 'clientdob', 'patientdob', 'memberdob']);
  const schemaKeys = new Set();
  const walkSchema = components => {
    if (!Array.isArray(components)) return;
    for (const component of components) {
      if (!component || typeof component !== 'object') continue;
      if (/\b(date\s*of\s*birth|birth\s*date|dob)\b/i.test(`${component.label || ''} ${component.title || ''}`) && component.key) {
        schemaKeys.add(normalizedKey(component.key));
      }
      walkSchema(component.components);
      for (const column of component.columns || []) walkSchema(column?.components);
      for (const row of component.rows || []) for (const cell of row || []) walkSchema(cell?.components);
    }
  };
  let parsedSchema = schema;
  if (typeof parsedSchema === 'string') {
    try { parsedSchema = JSON.parse(parsedSchema); } catch (_) { parsedSchema = null; }
  }
  walkSchema(parsedSchema?.components);
  const find = object => {
    for (const [key, value] of Object.entries(object || {})) {
      if (aliases.has(normalizedKey(key)) || schemaKeys.has(normalizedKey(key))) {
        const text = asText(value);
        if (text) return text;
      }
    }
    for (const value of Object.values(object || {})) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const text = find(value);
        if (text) return text;
      }
    }
    return '';
  };
  return find(data);
}

const valueFor = (object, keys) => {
  for (const key of keys) {
    const match = Object.entries(object || {}).find(([field]) => normalizedKey(field) === normalizedKey(key));
    const value = asText(match?.[1]);
    if (value) return value;
  }
  return '';
};

function parseAddressString(value) {
  const text = asText(value);
  if (!text) return null;
  const parts = text.split(',').map(part => part.trim()).filter(Boolean);
  if (/^(usa|u\.s\.a\.|us|united states)$/i.test(parts[parts.length - 1] || '')) parts.pop();
  const nonempty = parts.filter(Boolean);
  const stateZip = (parts[parts.length - 1] || '').match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (stateZip && parts.length >= 3) {
    return {
      address_line_1: parts[0],
      address_line_2: parts.length >= 4 ? parts[1] : '',
      city: parts[parts.length - 2], state: stateZip[1], zip: stateZip[2],
    };
  }
  if (parts.length >= 4 && /^\d{5}(?:-\d{4})?$/.test(parts[parts.length - 1]) && /^[A-Za-z]{2}$/.test(parts[parts.length - 2])) {
    return {
      address_line_1: parts[0], address_line_2: parts.length >= 5 ? parts[1] : '',
      city: parts[parts.length - 3], state: parts[parts.length - 2], zip: parts[parts.length - 1],
    };
  }
  return { address_line_1: nonempty.join(', '), address_line_2: '', city: '', state: '', zip: '' };
}

export function referralProspectAddress(data = {}) {
  const structured = [data._client_address, data.clientAddress, data.patientAddress, data.address]
    .find(value => value && typeof value === 'object' && !Array.isArray(value));

  if (structured) {
    const addressText = valueFor(data, ['address', 'clientAddressSingleLine', 'patientAddressSingleLine', 'fullAddress']);
    const parsed = parseAddressString(addressText) || {};
    return {
      address_line_1: valueFor(structured, ['address_line_1', 'addressLine1', 'street', 'address'])
        || valueFor(data, ['address_line_1', 'addressLine1', 'street']) || parsed.address_line_1 || '',
      address_line_2: valueFor(structured, ['address_line_2', 'addressLine2', 'unit', 'suite'])
        || valueFor(data, ['address_line_2', 'addressLine2', 'unit', 'suite']) || parsed.address_line_2 || '',
      city: valueFor(structured, ['city', 'locality', 'town'])
        || valueFor(data, ['city', 'locality', 'town']) || parsed.city || '',
      state: valueFor(structured, ['state', 'province', 'region'])
        || valueFor(data, ['state', 'province', 'region']) || parsed.state || '',
      zip: valueFor(structured, ['zip', 'postcode', 'postalCode', 'postal_code'])
        || valueFor(data, ['zip', 'postcode', 'postalCode', 'postal_code']) || parsed.zip || '',
    };
  }

  const address = valueFor(data, ['address', 'clientAddress', 'patientAddress', 'clientAddressSingleLine']);
  const parsed = parseAddressString(address) || { address_line_1: '', address_line_2: '', city: '', state: '', zip: '' };
  return {
    address_line_1: valueFor(data, ['address_line_1', 'addressLine1', 'street']) || parsed.address_line_1,
    address_line_2: valueFor(data, ['address_line_2', 'addressLine2', 'unit', 'suite']) || parsed.address_line_2,
    city: valueFor(data, ['city', 'locality', 'town']) || parsed.city,
    state: valueFor(data, ['state', 'province', 'region']) || parsed.state,
    zip: valueFor(data, ['zip', 'postcode', 'postalCode', 'postal_code']) || parsed.zip,
  };
}
